import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { database } from "../../firebase";
import { ref, onValue, off } from "firebase/database";
import { setSelectedCaseDetail } from "../../redux/casesRedux";
import useIsMobile from "../../hooks/useIsMobile";
import useCaseDetail from "../../hooks/useCaseDetail";
import CaseInformation from "./CaseInformation";
import ModelFiles from "./ModelFiles";
import CommentCaseModal from "../modals/CommentCaseModal";
import CommentsSection from "./CommentsSection";
import ShareModal from "../modals/ShareModal";
import UserPermissionModal from "../modals/UserPermissionModal";
import UploadSTLModal from "../modals/UploadSTLModal";
import UploadDICOMModal from "../modals/UploadDICOMModal";
import DeleteConfirmationModal from "../modals/DeleteConfirmationModal";
import DicomFiles from "./DicomFiles";
import { Statuses } from "../../constants/statuses";

const CaseDetail = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isMobile = useIsMobile();
  const selectedCase = useSelector((state) => state.cases.selectedCase);
  const selectedCaseDetail = useSelector(
    (state) => state.cases.selectedCaseDetail
  );
  const user = useSelector((state) => state.user);

  const [showSTLModal, setShowSTLModal] = useState(false);
  const [showDICOMModal, setShowDICOMModal] = useState(false);
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showUserPermissionModal, setShowUserPermissionModal] = useState(false);
  const [isUserDeleteModalOpen, setIsUserDeleteModalOpen] = useState(false);
  const [userToDeleteId, setUserToDeleteId] = useState(null);
  const [selectedComment, setSelectedComment] = useState(null);

  const {
    progress,
    uploading,
    success,
    handleSTLUpload,
    handleDICOMUpload,
    handleDeleteModel,
    handleSelectDicom,
    handleDeleteDicom,
    handleCommentSubmit,
    handleDeleteComment,
    handleShareAssignments,
    handleRoleChange,
    handleDeleteUser,
    handleSaveModelGroups,
  } = useCaseDetail(
    setShowSTLModal,
    setShowDICOMModal,
    setShowCommentModal,
    setSelectedComment
  );

  // Fetch case details from caseDetails collection
  useEffect(() => {
    if (selectedCase?.id) {
      const caseDetailRef = ref(database, `caseDetails/${selectedCase.id}`);

      onValue(caseDetailRef, (snapshot) => {
        const data = snapshot.val();
        dispatch(setSelectedCaseDetail(data));
      });

      return () => {
        off(caseDetailRef);
      };
    } else {
      // Clear selectedCaseDetail if no case is selected
      dispatch(setSelectedCaseDetail(null));
    }
  }, [selectedCase?.id, dispatch]);

  // Get modelInfos, dicoms, and modelGroups from selectedCaseDetail (items collection)
  const modelInfos = selectedCaseDetail?.modelInfos || [];
  const dicomInfos = selectedCaseDetail?.dicoms || [];
  const modelGroups = selectedCaseDetail?.modelGroups || {};

  const [files, setFiles] = useState([]);

  const userRole =
    user.isAdmin &&
    (selectedCase?.status === Statuses.IN_PROGRESS ||
      selectedCase?.status === Statuses.READY)
      ? "Admin"
      : selectedCase?.users?.[user?.currentUser?.id]?.role || "Viewer";

  const hasComments =
    !!selectedCaseDetail?.comments &&
    Object.keys(selectedCaseDetail.comments).length > 0;

  // File selection handlers
  const handleSTLFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files);
    if (!selectedFiles.length) return;
    setFiles(selectedFiles);
    setShowSTLModal(true);
    e.target.value = null;
  };

  const handleDICOMFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files);
    if (!selectedFiles.length) return;
    setFiles(selectedFiles);
    setShowDICOMModal(true);
    e.target.value = null;
  };

  const triggerUserDelete = (userId) => {
    setUserToDeleteId(userId);
    setIsUserDeleteModalOpen(true);
  };

  const closeUserDeleteModal = () => {
    setIsUserDeleteModalOpen(false);
    setUserToDeleteId(null);
  };

  const confirmUserDelete = async () => {
    await handleDeleteUser(userToDeleteId);
    closeUserDeleteModal();
  };

  // DICOM delete
  const confirmDicomDelete = async (idx) => {
    await handleDeleteDicom(idx);
  };

  // Comment edit
  const handleEditComment = (timestamp, comment) => {
    const c =
      comment ||
      (selectedCaseDetail?.comments && selectedCaseDetail.comments[timestamp]);
    if (!c) return;
    setSelectedComment({ text: c.text, timestamp });
    setShowCommentModal(true);
  };

  if (!selectedCase) return <p style={{ color: "#64748B" }}>Loading case details...</p>;

  return (
    <div className={isMobile ? "p-4" : "p-8"} style={{ minHeight: "100vh", backgroundColor: "#0B0E13" }}>
      {/* Main white content card */}
      <div
        style={{
          backgroundColor: "#FFFFFF",
          borderRadius: "16px",
          padding: isMobile ? "20px" : "28px 32px",
          boxShadow: "0 4px 32px rgba(0,0,0,0.4)",
          minHeight: "calc(100vh - 80px)",
        }}
      >
      <CaseInformation
        selectedCase={selectedCase}
        isMobile={isMobile}
        modelInfos={modelInfos}
        handleSTLFileSelect={handleSTLFileSelect}
        handleDICOMFileSelect={handleDICOMFileSelect}
        handleViewerClick={() => navigate(`/model/${selectedCase.id}`)}
        handleEditClick={() => navigate("/addoreditcasepage")}
        handleCommentCase={() => setShowCommentModal(true)}
        handleShare={() => setShowShareModal(true)}
        handleUserPermissions={() => setShowUserPermissionModal(true)}
        userRole={userRole}
        isDemo={selectedCase.isDemo}
      />

      {hasComments && <div className="border-t border-gray-200 mt-3 mb-3" />}

      <CommentsSection
        comments={selectedCaseDetail?.comments}
        onDelete={handleDeleteComment}
        onEdit={handleEditComment}
        user={user.currentUser}
        role={userRole}
        isMobile={isMobile}
      />

      <div style={{ height: "1px", backgroundColor: "#F1F5F9", margin: "12px 0" }} />

      <ModelFiles
        modelInfos={modelInfos}
        handleDownload={(file) => {
          const link = document.createElement("a");
          link.href = file.url;
          link.download = file.name;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }}
        handleDelete={handleDeleteModel}
        handleSaveModelGroups={handleSaveModelGroups}
        modelGroups={modelGroups}
        currentUserId={user?.currentUser?.id}
        isMobile={isMobile}
        userRole={userRole}
        isDemo={selectedCase.isDemo}
      />

      <div style={{ height: "1px", backgroundColor: "#F1F5F9", margin: "12px 0" }} />

      <DicomFiles
        dicomInfos={dicomInfos}
        handleDownload={(file) => {
          const link = document.createElement("a");
          link.href = file.url;
          link.download = file.name;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }}
        handleSelect={handleSelectDicom}
        handleDeleteDicom={confirmDicomDelete}
        isMobile={isMobile}
        userRole={userRole}
        isDemo={selectedCase.isDemo}
      />
      </div>

      {showSTLModal && (
        <UploadSTLModal
          files={files}
          progress={progress}
          uploading={uploading}
          success={success}
          onUpload={handleSTLUpload}
          onClose={() => setShowSTLModal(false)}
        />
      )}

      {showDICOMModal && (
        <UploadDICOMModal
          files={files}
          progress={progress}
          uploading={uploading}
          success={success}
          onUpload={() => handleDICOMUpload({ files })}
          onClose={() => setShowDICOMModal(false)}
        />
      )}

      {showCommentModal && (
        <CommentCaseModal
          onClose={() => {
            setSelectedComment(null);
            setShowCommentModal(false);
          }}
          caseId={selectedCase.id}
          comment={selectedComment}
          onSubmit={(text) =>
            handleCommentSubmit({ text, timestamp: selectedComment?.timestamp })
          }
        />
      )}

      {showShareModal && (
        <ShareModal
          onClose={() => setShowShareModal(false)}
          onSubmitAssignments={(assignments) =>
            handleShareAssignments({ assignments })
          }
        />
      )}

      {isUserDeleteModalOpen && (
        <DeleteConfirmationModal
          isOpen={isUserDeleteModalOpen}
          closeModal={closeUserDeleteModal}
          handleDelete={confirmUserDelete}
          entityName="their access to this case"
        />
      )}

      {showUserPermissionModal && (
        <UserPermissionModal
          onClose={() => setShowUserPermissionModal(false)}
          selectedCase={selectedCase}
          userRole={userRole}
          onRoleChange={handleRoleChange}
          handleUserDelete={triggerUserDelete}
        />
      )}
    </div>
  );
};

export default CaseDetail;
